import { Activity, Clock, Phone } from "lucide-react";
import { useMemo } from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { AdminDailyCallStats } from "../contexts/DataContext";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";

function formatDuration(seconds: number) {
	if (!seconds || seconds <= 0) return "0s";
	if (seconds < 60) return `${Math.round(seconds)}s`;
	const m = Math.floor(seconds / 60);
	const s = Math.round(seconds % 60);
	if (m < 60) return `${m}m ${s}s`;
	const h = Math.floor(m / 60);
	const mm = m % 60;
	return `${h}h ${mm}m`;
}

/** Returns a YYYY-MM-DD string for the local calendar date `daysAgo` days ago. */
function localDateKeyOffset(daysAgo: number): string {
	const d = new Date();
	d.setDate(d.getDate() - daysAgo);
	return [
		d.getFullYear(),
		String(d.getMonth() + 1).padStart(2, "0"),
		String(d.getDate()).padStart(2, "0"),
	].join("-");
}

interface AdminCallStatsProps {
	stats: AdminDailyCallStats[];
}

export function AdminCallStats({ stats }: AdminCallStatsProps) {
	const days = 30;
	const cutoff = useMemo(() => localDateKeyOffset(days - 1), []);

	const { totalCalls, totalDuration, chartData } = useMemo(() => {
		// Initialize the last 30 days
		const daysMap = new Map<string, { calls: number; duration: number }>();
		const today = new Date();
		for (let i = days - 1; i >= 0; i--) {
			const d = new Date(today);
			d.setDate(d.getDate() - i);
			const dk = [
				d.getFullYear(),
				String(d.getMonth() + 1).padStart(2, "0"),
				String(d.getDate()).padStart(2, "0"),
			].join("-");
			daysMap.set(dk, { calls: 0, duration: 0 });
		}

		let totalC = 0;
		let totalD = 0;

		for (const doc of stats) {
			if (doc.dateKey < cutoff) continue;

			const c = doc.totals?.overall?.callCount || 0;
			const d = doc.totals?.overall?.durationSeconds || 0;

			totalC += c;
			totalD += d;

			if (daysMap.has(doc.dateKey)) {
				const current = daysMap.get(doc.dateKey)!;
				current.calls += c;
				current.duration += Math.round(d / 60); // Store duration in minutes for chart
			}
		}

		const cData = Array.from(daysMap.entries()).map(([date, vals]) => {
			// Format date from YYYY-MM-DD to "Mon 01"
			const d = new Date(date + "T12:00:00Z"); // Use noon UTC to avoid timezone shift locally
			const formatted = d.toLocaleDateString("en-US", {
				month: "short",
				day: "numeric",
			});
			return {
				date: formatted,
				Calls: vals.calls,
				"Duration (min)": vals.duration,
			};
		});

		return { totalCalls: totalC, totalDuration: totalD, chartData: cData };
	}, [stats, cutoff]);

	const avgDuration = totalCalls > 0 ? totalDuration / totalCalls : 0;

	return (
		<div className="space-y-6">
			<div className="grid gap-4 md:grid-cols-3">
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Total Calls</CardTitle>
						<Phone className="h-4 w-4 text-slate-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-slate-900">
							{totalCalls.toLocaleString()}
						</div>
						<p className="text-xs text-slate-500 mt-1">Last {days} days</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">
							Total Duration
						</CardTitle>
						<Activity className="h-4 w-4 text-slate-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-slate-900">
							{formatDuration(totalDuration)}
						</div>
						<p className="text-xs text-slate-500 mt-1">Last {days} days</p>
					</CardContent>
				</Card>
				<Card>
					<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
						<CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
						<Clock className="h-4 w-4 text-slate-500" />
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-slate-900">
							{formatDuration(avgDuration)}
						</div>
						<p className="text-xs text-slate-500 mt-1">Per call average</p>
					</CardContent>
				</Card>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Daily Call Volume & Duration</CardTitle>
					<CardDescription>
						Volume and total duration in minutes over the last {days} days
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="h-72 w-full mt-4">
						<ResponsiveContainer width="100%" height="100%">
							<BarChart
								data={chartData}
								margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
							>
								<CartesianGrid
									strokeDasharray="3 3"
									vertical={false}
									stroke="#E2E8F0"
								/>
								<XAxis
									dataKey="date"
									axisLine={false}
									tickLine={false}
									tick={{ fontSize: 12, fill: "#64748B" }}
									dy={10}
								/>
								<YAxis
									yAxisId="left"
									axisLine={false}
									tickLine={false}
									tick={{ fontSize: 12, fill: "#64748B" }}
									dx={-10}
								/>
								<YAxis
									yAxisId="right"
									orientation="right"
									axisLine={false}
									tickLine={false}
									tick={{ fontSize: 12, fill: "#64748B" }}
									dx={10}
								/>
								<Tooltip
									cursor={{ fill: "#F1F5F9" }}
									contentStyle={{
										borderRadius: "8px",
										border: "1px solid #E2E8F0",
										boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
									}}
								/>
								<Legend wrapperStyle={{ paddingTop: "20px" }} />
								<Bar
									yAxisId="left"
									dataKey="Calls"
									fill="#0EA5E9"
									radius={[4, 4, 0, 0]}
								/>
								<Bar
									yAxisId="right"
									dataKey="Duration (min)"
									fill="#6366F1"
									radius={[4, 4, 0, 0]}
								/>
							</BarChart>
						</ResponsiveContainer>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
